import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminOrderRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IAccountResponse } from '../models/account.model';
import { AccountApiService } from '../services/account-api.service';
import {
  CreateAccount,
  DeleteAccount,
  LoadAccountById,
  LoadAccounts,
  LoadAllAccounts,
  UpdateAccount,
  ClearCurrentAccount,
} from './account.actions';

export interface AccountStateModel {
  accountsLoaded: boolean;
  accounts: IAccountResponse[];
  allAccounts: IAccountResponse[];
  currentAccount: IAccountResponse | undefined;
  accountsTotalPages: number;
}

const ACCOUNT_STATE_TOKEN = new StateToken<AccountStateModel>('account');

@State<AccountStateModel>({
  name: ACCOUNT_STATE_TOKEN,
  defaults: {
    accountsLoaded: false,
    accounts: [],
    allAccounts: [],
    accountsTotalPages: 0,
    currentAccount: undefined,
  },
})
@Injectable()
export class AccountState {
  constructor(private service: AccountApiService) {}

  @Action(LoadAccounts)
  loadAccounts({ getState, setState }: StateContext<AccountStateModel>, { filter }: LoadAccounts) {
    return this.service.loadAccounts(filter).pipe(
      tap((response: ApiResponse<IAccountResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          accountsLoaded: true,
          accounts: response.data,
          accountsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllAccounts, { cancelUncompleted: true })
  loadAllAccounts(ctx: StateContext<AccountStateModel>) {
    return this.service.loadAllAccounts().pipe(
      tap((allAccounts: IAccountResponse[]) => {
        ctx.setState(patch({ allAccounts }));
      })
    );
  }

  @Action(LoadAccountById, { cancelUncompleted: true })
  loadAccountById({ setState }: StateContext<AccountStateModel>, { id }: LoadAccountById) {
    return this.service.loadAccountById(id).pipe(
      tap((account: IAccountResponse) => {
        setState(
          patch({
            currentAccount: account,
          })
        );
      })
    );
  }

  @Action(CreateAccount)
  @OnSuccess({ url: AdminOrderRoutes.Accounts, message: 'accountCreated' })
  createAccount({ setState }: StateContext<AccountStateModel>, { account }: CreateAccount) {
    return this.service.createAccount(account).pipe(
      tap((response: IAccountResponse) => {
        setState(
          patch({
            accounts: insertItem<IAccountResponse>(response),
          })
        );
      })
    );
  }

  @Action(UpdateAccount)
  @OnSuccess({ url: AdminOrderRoutes.Accounts, message: 'accountUpdated' })
  updateAccount({ setState }: StateContext<AccountStateModel>, { account }: UpdateAccount) {
    return this.service.updateAccount(account);
  }

  @Action(DeleteAccount, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminOrderRoutes.Accounts,
    message: 'accountDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteAccount(ctx: StateContext<AccountStateModel>, { id }: DeleteAccount) {
    return this.service.deleteAccount(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            accounts: removeItem<IAccountResponse>(
              (jtToDelete: IAccountResponse | undefined) => jtToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentAccount)
  clearCurrentAccount(ctx: StateContext<AccountStateModel>) {
    ctx.setState(patch({ currentAccount: undefined }));
  }

  @Selector()
  static getAccountsTotalPages(state: AccountStateModel) {
    return state.accountsTotalPages;
  }

  @Selector()
  static accounts(state: AccountStateModel) {
    return state.accounts;
  }

  @Selector()
  static allAccounts(state: AccountStateModel) {
    return state.allAccounts;
  }

  @Selector()
  static getCurrentAccount(state: AccountStateModel) {
    return state.currentAccount;
  }
}
